import { Card, CardBody, Col, FormGroup, Input, Label, Row, Spinner } from "reactstrap";
import Select from "react-select";
import { BsBuildings, BsCamera } from "react-icons/bs";
import SidebarLayout from "../../../../layouts/SidebarLayout";
import sidebar from "../../utils/sidebar";
import PageSubHeader from "../../../../components/Layouts/PageSubHeader";
import './styles.css';
import { useEffect, useRef, useState } from "react";
import { get, postForm } from "../../../../utils/helpers/api";
import countries, { countryCodeOptions } from "../../../../data/countries";
import SelectCustomStyles from "../../../../utils/styles/reactSelect";
import { AWS_S3_URL } from "../../../../utils/constants";
import DefaultButton from "../../../../components/Buttons/Default";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";

function CompanyPage() {
  const { t } = useTranslation();
  const [companyData, setCompanyData] = useState({
    logo: null,
    display_name: '',
    legal_name: '',
    email: '',
    country_code: '',
    phone_number: '',
    industry: '',
    size: '',
    website: '',
    street_address: '',
    city: '',
    state: '',
    postal_code: '',
    country: '',
    tax_id: '',
    registration_number: '',
    nis: '',
    cnas_employer_number: '',
    tax_article: ''
  });
  const [companyLoading, setCompanyLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const logoInputRef = useRef(null);

  const getCompany = async () => {
    setCompanyLoading(true);

    const data = await get('/settings/company');
    if(data?.company) {
      setCompanyData({
        logo: data.company.logo_path,
        display_name: data.company.display_name,
        legal_name: data.company.legal_name,
        email: data.company.email,
        country_code: data.company.country_code,
        phone_number: data.company.phone_number,
        industry: data.company.industry,
        size: data.company.size,
        website: data.company.website,
        street_address: data.company.street_address,
        city: data.company.city,
        state: data.company.state,
        postal_code: data.company.postal_code,
        country: data.company.country,
        tax_id: data.company.tax_id,
        registration_number: data.company.registration_number,
        nis: data.company.nis,
        cnas_employer_number: data.company.cnas_employer_number,
        tax_article: data.company.tax_article
      });
    }
    setCompanyLoading(false);
  }

  useEffect(() => {
    getCompany();
  }, []);

  const saveCompany = async () => {
    setSaveLoading(true);

    const formData = new FormData();
    formData.append('display_name', companyData?.display_name ?? '');
    formData.append('legal_name', companyData?.legal_name ?? '');
    formData.append('email', companyData?.email ?? '');
    formData.append('country_code', companyData?.country_code ?? '');
    formData.append('phone_number', companyData?.phone_number ?? '');
    formData.append('industry', companyData?.industry ?? '');
    formData.append('size', companyData?.size ?? '');
    formData.append('website', companyData?.website ?? '');
    formData.append('street_address', companyData?.street_address ?? '');
    formData.append('city', companyData?.city ?? '');
    formData.append('state', companyData?.state ?? '');
    formData.append('postal_code', companyData?.postal_code ?? '');
    formData.append('country', companyData?.country ?? '');
    formData.append('tax_id', companyData?.tax_id ?? '');
    formData.append('registration_number', companyData?.registration_number ?? '');
    formData.append('nis', companyData?.nis ?? '');
    formData.append('cnas_employer_number', companyData?.cnas_employer_number ?? '');
    formData.append('tax_article', companyData?.tax_article ?? '');

    if (logoFile) {
      formData.append('logo', logoFile);
    }

    Promise.resolve(postForm('/settings/company/update', formData))
    .then(data => {
      if(data.success) {
        toast.success(t('settings.companyInfoUpdated'));
      } else {
        toast.error(data.message || t('settings.failedUpdateCompany'));
      }
      setSaveLoading(false);
    })
    .catch(() => {
      toast.error(t('settings.failedUpdateCompany'));
      setSaveLoading(false);
    });
  }

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setLogoFile(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const displayLogo = logoPreview || `${AWS_S3_URL}${companyData.logo}`;

  return(
    <SidebarLayout sidebar={sidebar}>
      <div className="page-with-subheader">
        <PageSubHeader title={t('settings.company')} />

        {companyLoading ?
          <Spinner color="primary" className="d-block mx-auto my-4" />
        :
          <div className="subheader-page-content scrollable">
            {/* Basic Information */}
            <Card className="mb-3">
              <CardBody>
                <p className="text-muted small fw-semibold text-uppercase mb-3">{t('settings.basicInfo')}</p>
                <div className="d-flex align-items-start gap-3 flex-wrap">
                  <div className="text-center mb-3 mb-lg-0">
                    <Label className="d-block mb-2">{t('settings.logo')}</Label>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/*"
                      className="d-none"
                      onChange={handleLogoChange}
                    />
                    <div
                      className="company-logo-picker mx-auto"
                      onClick={() => logoInputRef.current.click()}
                    >
                      {displayLogo ? (
                        <img
                          src={displayLogo}
                          alt={t('settings.companyLogoAlt')}
                          className="company-logo-preview"
                        />
                      ) : (
                        <BsBuildings size={66} />
                      )}
                      <div className="company-logo-overlay">
                        <BsCamera size={24} color="#fff" />
                      </div>
                    </div>
                    <small className="text-muted d-block mt-2">{t('profile.clickToUpload')}</small>
                  </div>
                  <div className="flex-grow-1">
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <Label>{t('settings.displayName')}</Label>
                          <Input
                            type="text"
                            value={companyData.display_name}
                            onChange={(e) => setCompanyData({ ...companyData, display_name: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <Label>{t('settings.legalName')}</Label>
                          <Input
                            type="text"
                            value={companyData.legal_name}
                            onChange={(e) => setCompanyData({ ...companyData, legal_name: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <Label>{t('settings.emailAddress')}</Label>
                          <Input
                            type="email"
                            value={companyData.email}
                            onChange={(e) => setCompanyData({ ...companyData, email: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <Label>{t('settings.phone')}</Label>
                          <div className="d-flex gap-2">
                            <div style={{ width: 140, flexShrink: 0 }}>
                              <Select
                                options={countryCodeOptions}
                                value={countryCodeOptions.find((o) => o.value === companyData.country_code) || null}
                                onChange={(selected) =>
                                  setCompanyData({ ...companyData, country_code: selected ? selected.value : "" })
                                }
                                styles={SelectCustomStyles}
                              />
                            </div>
                            <Input
                              type="tel"
                              name="phone_number"
                              placeholder={t('settings.phoneNumber')}
                              value={companyData.phone_number}
                              onChange={(e) => setCompanyData({ ...companyData, phone_number: e.target.value })}
                            />
                          </div>
                        </FormGroup>
                      </Col>
                    </Row>
                    <Row>
                      <Col lg="6">
                        <FormGroup>
                          <Label>{t('settings.industry')}</Label>
                          <Input
                            type="text"
                            value={companyData.industry}
                            onChange={(e) => setCompanyData({ ...companyData, industry: e.target.value })}
                          />
                        </FormGroup>
                      </Col>
                      <Col lg="6">
                        <FormGroup>
                          <Label>{t('settings.companySize')}</Label>
                          <Input type="select" value={companyData.size} onChange={(e) => setCompanyData({ ...companyData, size: e.target.value })}>
                            <option value="">{t('common.select')}</option>
                            <option value="1-10">{t('settings.employees1to10')}</option>
                            <option value="11-50">{t('settings.employees11to50')}</option>
                            <option value="51-200">{t('settings.employees51to200')}</option>
                            <option value="201-500">{t('settings.employees201to500')}</option>
                            <option value="501-1000">{t('settings.employees501to1000')}</option>
                            <option value="1001+">{t('settings.employees1001plus')}</option>
                          </Input>
                        </FormGroup>
                      </Col>
                    </Row>
                    <FormGroup className="mb-0">
                      <Label>{t('settings.website')}</Label>
                      <Input
                        type="url"
                        value={companyData.website}
                        onChange={(e) => setCompanyData({ ...companyData, website: e.target.value })}
                        placeholder="https://"
                      />
                    </FormGroup>
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Address */}
            <Card className="mb-3">
              <CardBody>
                <p className="text-muted small fw-semibold text-uppercase mb-3">{t('settings.addressInfo')}</p>
                <FormGroup>
                  <Label>{t('settings.streetAddress')}</Label>
                  <Input
                    placeholder={t('settings.streetAddressPlaceholder')}
                    value={companyData.street_address}
                    onChange={(e) => setCompanyData({ ...companyData, street_address: e.target.value })}
                  />
                </FormGroup>
                <Row>
                  <Col lg="6">
                    <FormGroup>
                      <Label>{t('settings.city')}</Label>
                      <Input
                        type="text"
                        value={companyData.city}
                        onChange={(e) => setCompanyData({ ...companyData, city: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup>
                      <Label>{t('settings.state')}</Label>
                      <Input
                        type="text"
                        value={companyData.state}
                        onChange={(e) => setCompanyData({ ...companyData, state: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                </Row>
                <Row className="mb-0">
                  <Col lg="6">
                    <FormGroup className="mb-0">
                      <Label>{t('settings.postalCode')}</Label>
                      <Input
                        type="text"
                        value={companyData.postal_code}
                        onChange={(e) => setCompanyData({ ...companyData, postal_code: e.target.value })}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="6">
                    <FormGroup className="mb-0">
                      <Label>{t('settings.country')}</Label>
                      <Select
                        value={countries.map(c => ({ value: c.slug, label: c.name })).find(o => o.value === companyData.country) || null}
                        onChange={(selected) => setCompanyData({ ...companyData, country: selected ? selected.value : '' })}
                        options={
                          countries.map((country) => (
                            { value: country.slug, label: country.name }
                          ))
                        }
                        styles={SelectCustomStyles}
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            {/* Legal & Tax */}
            <Card className="mb-3">
              <CardBody>
                <p className="text-muted small fw-semibold text-uppercase mb-3">{t('settings.legalTax')}</p>
                <Row className="mb-0">
                  <Col lg="4">
                    <FormGroup className="mb-0">
                      <Label>{t('settings.taxVatNumber')}</Label>
                      <Input
                        type="text"
                        value={companyData.tax_id}
                        onChange={(e) => setCompanyData({ ...companyData, tax_id: e.target.value })}
                        placeholder={t('settings.taxIdPlaceholder')}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="4">
                    <FormGroup className="mb-0">
                      <Label>{t('settings.registrationNumber')}</Label>
                      <Input
                        type="text"
                        value={companyData.registration_number}
                        onChange={(e) => setCompanyData({ ...companyData, registration_number: e.target.value })}
                        placeholder={t('settings.registrationNumberPlaceholder')}
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="4">
                    <FormGroup className="mb-0 mt-lg-0">
                      <Label>{t('settings.nis')}</Label>
                      <Input
                        type="text"
                        value={companyData.nis}
                        onChange={(e) => setCompanyData({ ...companyData, nis: e.target.value })}
                        placeholder="000216001234567"
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="4">
                    <FormGroup className="mb-0 mt-3">
                      <Label>{t('legal.taxArticle')}</Label>
                      <Input
                        type="text"
                        value={companyData.tax_article}
                        onChange={(e) => setCompanyData({ ...companyData, tax_article: e.target.value })}
                        placeholder="16123456789"
                      />
                    </FormGroup>
                  </Col>
                  <Col lg="4">
                    <FormGroup className="mb-0 mt-3">
                      <Label>{t('settings.cnasEmployerNumber')}</Label>
                      <Input
                        type="text"
                        value={companyData.cnas_employer_number}
                        onChange={(e) => setCompanyData({ ...companyData, cnas_employer_number: e.target.value })}
                        placeholder="1601234567"
                      />
                    </FormGroup>
                  </Col>
                </Row>
              </CardBody>
            </Card>

            <DefaultButton
              color="primary"
              onClick={saveCompany}
              disabled={companyLoading || saveLoading}
              loading={saveLoading}
            >
              {t('common.save')}
            </DefaultButton>
          </div>
        }
      </div>
    </SidebarLayout>
  );
}

export default CompanyPage;
